import { Button } from '@insurancemenu/imenu-components-lib';
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from '@insurancemenu/imenu-components-lib/dist/icons';
import { ButtonPropsType, ICustomizableElement } from '@insurancemenu/imenu-components-lib/dist/types';
import clsx from 'clsx';
import { createStyles } from './styles';

export interface CollapsibleSectionProps extends ICustomizableElement {
  /**
   * The title of the section, which also serves as the button to toggle the section.
   */
  title: string | JSX.Element;
  /*
   * Additional props for the button component used as the section title.
   * These can include styling, disabled status or other button-related attributes.
   */
  buttonProps?: Omit<ButtonPropsType, 'children'>;
  /**
   * Determines whether the section should be open by default when the component is first rendered.
   */
  isOpenByDefault?: boolean;
  /**
   * If true, the section title will be pinned to the top of the section with a shadow effect.
   * This allows users to collapse the section without having to scroll back to the top,
   * which is useful for sections with long content.
   */
  pinTitle?: boolean;
  /**
   * A unique identifier for testing purposes.
   */
  'data-test-id'?: string;
  children?: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = (props) => {
  const { children, title, className, isOpenByDefault = true, buttonProps = {}, pinTitle = false } = props;
  const [isSectionOpen, setIsSectionOpen] = useState(isOpenByDefault);

  const classes = createStyles({ isSectionOpen });

  const onToggle = (): void => {
    setIsSectionOpen(!isSectionOpen);
  };

  return (
    <div className={className} data-test-id={props['data-test-id']}>
      <Button
        type="text"
        className={clsx(classes.title, pinTitle && classes.stickyTitle)}
        rightIconProp={
          isSectionOpen ? <ChevronUp data-test-id="chevron-up" /> : <ChevronDown data-test-id="chevron-down" />
        }
        iconSize="3rem"
        onClick={onToggle}
        {...buttonProps}
      >
        {title}
      </Button>
      {!isSectionOpen && !buttonProps?.disabled && (
        <p className={classes.sectionGuideline}>Click the title to access the content.</p>
      )}
      <div className={clsx(classes.section, !isSectionOpen && classes.closedSection)}>{children}</div>
    </div>
  );
};

export default CollapsibleSection;
